import { Link } from "react-router";

import { Badge } from "@/components/ui/badge";
import { LOAD_STATUS_CHOICES } from "../loads/LoadInputs";
import type { Load } from "../types";

export type DashboardLoad = Load & {
  customer_company_name?: string;
  customer_date_paid?: string | null;
  customer_total_usd?: number;
  customer_invoice_total?: number;
  vendor_company_name?: string;
  vendor_date_paid?: string | null;
  vendor_total_usd?: number;
  vendor_invoice_total?: number;
  margin?: string | number;
};

const progression = LOAD_STATUS_CHOICES.filter(
  ({ id }) =>
    !["pca_hold", "logistics_hold", "sos", "closed_lost"].includes(id),
);

const getLoadProgress = (status: string) => {
  const index = progression.findIndex(({ id }) => id === status);
  return { completed: index + 1, total: progression.length };
};

const currency = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});

export const DashboardLoadCard = ({
  load,
  to,
  selected = false,
}: {
  load: DashboardLoad;
  to?: string;
  selected?: boolean;
}) => {
  const status =
    LOAD_STATUS_CHOICES.find(({ id }) => id === load.status)?.name ??
    load.status;
  const progress = getLoadProgress(load.status);
  const exception = progress.completed === 0;
  const statusClass =
    load.status === "closed_lost" || load.status === "sos"
      ? "bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-200"
      : exception
        ? "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200"
        : "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200";

  return (
    <Link
      to={to ?? `/loads/${load.id}/show`}
      preventScrollReset
      aria-current={selected ? "true" : undefined}
      className={`block min-w-0 rounded-xl border bg-card p-4 text-card-foreground shadow-sm transition-colors hover:border-primary/50 hover:bg-muted/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${selected ? "border-primary bg-primary/5 ring-1 ring-primary" : ""}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <span className="text-base font-semibold text-primary">
          Load {load.load_number || load.id}
        </span>
        <Badge variant="secondary" className={statusClass}>
          {status}
        </Badge>
      </div>
      <dl className="mt-4 grid min-w-0 gap-2 text-sm">
        <div className="grid grid-cols-[5rem_minmax(0,1fr)] gap-2">
          <dt className="text-muted-foreground">Vendor</dt>
          <dd className="truncate font-medium" title={load.vendor_company_name}>
            {load.vendor_company_name || "—"}
          </dd>
        </div>
        <div className="grid grid-cols-[5rem_minmax(0,1fr)] gap-2">
          <dt className="text-muted-foreground">Customer</dt>
          <dd
            className="truncate font-medium"
            title={load.customer_company_name}
          >
            {load.customer_company_name || "—"}
          </dd>
        </div>
        <div className="grid grid-cols-[5rem_minmax(0,1fr)] gap-2">
          <dt className="text-muted-foreground">Pickup</dt>
          <dd>
            {load.pickup_date
              ? new Date(`${load.pickup_date}T00:00:00`).toLocaleDateString()
              : "—"}
          </dd>
        </div>
      </dl>
      <div
        className="mt-4 flex gap-1"
        role="img"
        aria-label={
          exception
            ? `Status: ${status}. Outside standard progression.`
            : `${status}: step ${progress.completed} of ${progress.total}`
        }
      >
        {progression.map((step, index) => (
          <span
            key={step.id}
            title={step.name}
            aria-hidden="true"
            className={`h-1.5 flex-1 rounded-full ${index < progress.completed ? "bg-emerald-600 dark:bg-emerald-500" : "bg-muted"}`}
          />
        ))}
      </div>
      <dl className="mt-4 grid grid-cols-2 gap-4 border-t pt-3">
        <div>
          <dt className="text-xs text-muted-foreground">Profit (USD)</dt>
          <dd
            className={`mt-1 font-semibold tabular-nums ${(load.profit_usd ?? 0) < 0 ? "text-destructive" : ""}`}
          >
            {load.profit_usd == null ? "—" : currency.format(load.profit_usd)}
          </dd>
        </div>
        <div className="text-right">
          <dt className="text-xs text-muted-foreground">Margin</dt>
          <dd className="mt-1 font-semibold tabular-nums">
            {load.margin == null || load.margin === ""
              ? "—"
              : `${Number(load.margin).toFixed(1)}%`}
          </dd>
        </div>
      </dl>
    </Link>
  );
};
